import React from "react";
import styled from "styled-components";
import { compose, withHandlers } from "recompose";
import { StripeProvider, Elements } from "react-stripe-elements";
import { STRIPE_PK } from "../../../../constants";
import withTopNineDetail from "../../core/withTopNineDetail";
import updateTopNineOrder from "../../libs/updateTopNineOrder";
import { withTopNineVideoModal } from "../../core/withVideo";
import PaymentRequest from "../common/Checkout/PaymentRequest";
import Header from "../common/Header";
import loadingImage from "../../../images/9load.gif";
import Grid, { HOLDER_WIDTH } from "./Grid";
import UnlockTopNine from "./UnlockTopNine";
import StoryActions from "./StoryActions";
import CustomizeGridModal from "./CustomizeGrid";
import BlockingGridModal from "./BlockingGridModal";
import VideoModal from "../Video";
import LoadingGridState from "./LoadingState";

const width = window.outerWidth;
const TOP_NINE_MARGIN = 20;
const ITEM_SIZE = (width - TOP_NINE_MARGIN * 2) / 3;

const TopNineHeader = styled(Header)`
  /* padding: 16px 0; */
`;

const TopNine = props => {
  if (props.error) {
    return (
      <React.Fragment>
        <TopNineHeader />
        <Container>
          <ErrorMessage>{props.error}</ErrorMessage>
        </Container>
      </React.Fragment>
    );
  }
  const isLoading = !props.items || props.items.length === 0 || !props.gridDelayReady;
  const showStoryActions = !props.paymentRequired || props.checkoutStatus === "success";
  const unlockTopNine = props.paymentRequired || props.checkoutStatus === "success";
  const showStats = !isLoading && props.totalPost > 0;
  return (
    <StripeProvider apiKey={STRIPE_PK}>
      <React.Fragment>
        <Elements>
          <PaymentRequest setCanMakePaymentWithButton={props.setCanMakePaymentWithButton} />
        </Elements>
        <TopNineHeader />
        <Container>
          {isLoading && showStoryActions && (
            <LoadingContainer>
              <LoadingGif src={loadingImage} />
              {props.checkoutStatus !== "success" ? (
                <SpinnerText>
                  Your Top Nine could take a few seconds to generate.
                  <br />
                  {!props.email || !props.name
                    ? null
                    : <React.Fragment>
                        Don't worry! We will email the Top Nine image
                        for <b>@{props.name + " "}</b>
                        to <b>{props.email}</b>.
                      </React.Fragment>
                  }
                </SpinnerText>
              ) : (
                <SpinnerText>Building your Top Nine</SpinnerText>
              )}
            </LoadingContainer>
          )}
          {/*isLoading && <LoadingGridState/>*/}
          {isLoading ? null :
            <React.Fragment>
              <Elements>
                <PaymentRequest onTokenSuccess={props.onWatermarkTokenSuccess} amount={props.watermarkPrice}>
                  {({ canMakePayment, showPaymentRequest }) => (
                    <React.Fragment>
                      <Grid
                        items={props.items}
                        lockedWatermark={props.lockedWatermark}
                        onClickGrid={canMakePayment ? showPaymentRequest : null}
                      />
                      <SubContainer>
                        {showStats && (
                          <DataContainer>
                            <LikesContainer>
                              <Likes isLoading={isLoading}>You received </Likes>
                              <LikesBold isLoading={isLoading}>{props.likes}</LikesBold>
                              <Likes isLoading={isLoading}> likes in 2020! 🎉</Likes>
                            </LikesContainer>
                            <InfoContainer isLoading={isLoading}>
                              <InfoSubContainer>
                                <InfoNumber isLoading={isLoading}>{props.totalPost}</InfoNumber>
                                <InfoTitle isLoading={isLoading}>posts</InfoTitle>
                              </InfoSubContainer>
                              <Separator isLoading={isLoading} />
                              <InfoSubContainer>
                                <InfoNumber isLoading={isLoading}>{props.likes}</InfoNumber>
                                <InfoTitle isLoading={isLoading}> likes</InfoTitle>
                              </InfoSubContainer>
                              <Separator isLoading={isLoading} />
                              <InfoSubContainer>
                                <InfoNumber isLoading={isLoading}>{props.likesPerPost}</InfoNumber>
                                <InfoTitle isLoading={isLoading}>likes per post</InfoTitle>
                              </InfoSubContainer>
                            </InfoContainer>
                          </DataContainer>
                        )}
                        {showStoryActions && (
                          <StoryActions
                            width={HOLDER_WIDTH}
                            withWatermark={props.withWatermark}
                            isLoading={isLoading}
                            storyUrl={props.storyUrl}
                            status={props.checkoutWatermarkStatus}
                            isCreatingNewStory={props.isCreatingNewStory}
                            openCustomizeModal={props.toggleCustomizeModal}
                            openVideoModal={props.openVideoModal}
                            watermarkPrice={props.watermarkPrice}
                            onStatusChange={props.onCheckoutWatermarkStatusChange}
                            refreshCurrentUser={props.refreshCurrentUser}
                            userName={props.name}
                            userEmail={props.email}
                            videoFeatureEnabled={props.videoFeatureEnabled}
                            onClickWithPaymentRequest={canMakePayment ? showPaymentRequest : null}
                          />
                        )}
                        {unlockTopNine && (
                          <UnlockTopNine
                            user={props.checkout}
                            status={props.checkoutStatus}
                            paymentRequired={props.paymentRequired}
                            onStatusChange={props.onCheckoutStatusChange}
                            refreshCurrentUser={props.refreshCurrentUser}
                            unlockTopNinePrice={props.unlockTopNinePrice}
                          />
                        )}
                      </SubContainer>
                    </React.Fragment>
                  )}
                </PaymentRequest>
              </Elements>
            </React.Fragment>
          }
          <CustomizeGridModal
            open={props.customizeModalOpen}
            onRequestClose={props.toggleCustomizeModal}
            setStorySettings={props.setStorySettings}
            storySettings={props.storySettings}
          />
          <VideoModal
            topNineSource={props.source}
            open={props.videoModalOpen}
            onRequestClose={props.closeVideoModal}
            userName={props.name}
            username={props.name}
            userEmail={props.email}
            totalLikes={props.likes}
            isVideoLocked={props.isVideoLocked}
            videoPrice={props.videoPrice}
            refetchUserPurchases={props.refetchUserPurchases}
          />
          <BlockingGridModal open={props.openBlockingModal} country={props.country} />
        </Container>
      </React.Fragment>
    </StripeProvider>
  );
};

const Container = styled.div`
  flex: 1;
  align-items: center;
  display: flex;
  flex-direction: column;
`;

const LoadingContainer = styled.div`
  width: 100%;
  display: flex;
  margin-top: 100px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: ${ITEM_SIZE * 3 + 3};
`;

const LoadingGif = styled.img`
  height: 50px;
  width: 50px;
  margin-bottom: 50px;
`;

const SpinnerText = styled.p`
  color: #6e788d;
  font-size: 15px;
  font-family: Montserrat;
  font-weight: 300;
  margin: 0 10px;
  margin-top: 15px;
  text-align: center;
  max-width: 95%;
  b {
    color: #f74255;
  }
`;

const SubContainer = styled.div`
  flex: 1;
  width: 100%;
  display: flex;
  justify-content: space-around;
  flex-direction: column;
  align-items: center;
`;

const LikesContainer = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: end;
  white-space: pre-wrap;
  flex-direction: row;
  align-items: center;
  margin-bottom: 10px;
`;

const Likes = styled.p`
  background-color: transparent;
  text-align: center;
  color: ${props => (props.isLoading ? "white" : "#303030")};
  font-size: 14px;
  font-family: Montserrat;
  font-weight: 300;
`;

const LikesBold = styled(Likes)`
  font-family: Montserrat;
  font-weight: 600;
`;

const DataContainer = styled.div`
  margin-top: 16px;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  align-items: center;
  max-width: 400px;
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 40px;
  width: 80%;
  border-radius: 50px;
  border: 1px solid #cdcdcd;
  ${props => (props.isLoading ? "display: none" : "")};
`;

const InfoSubContainer = styled.div`
  align-items: center;
  justify-content: center;
  flex: 1;
`;

const Separator = styled.div`
  background-color: #cdcdcd;
  width: 1px;
  height: 70%;
  ${props => (props.isLoading ? "display: none" : "")};
`;

const InfoNumber = styled(Likes)`
  font-size: 14px;
`;

const InfoTitle = styled(InfoNumber)`
  color: #8c8c8c;
  font-size: 10px;
  ${props => (props.isLoading ? "display: none" : "")};
`;

const ErrorMessage = styled.p`
  opacity: 0.7;
  margin-top: 48px;
`;

const enhancer = compose(
  withTopNineDetail,
  withTopNineVideoModal,
  withHandlers({
    onWatermarkTokenSuccess: props => token => {
      updateTopNineOrder({
        stripe: {
          token: token.id,
        },
      })
        .then(() => {
          props.onCheckoutWatermarkStatusChange("success");
          props.refreshCurrentUser();
          if (props.onClose) {
            props.onClose();
          }
        })
        .catch(() => {
          props.onCheckoutWatermarkStatusChange("error");
        });
    },
  })
);

export default enhancer(TopNine);
